/**
 * ContentItemList Component
 * Displays content items in a table/list format
 */

import { useState, useCallback } from 'react'
import {
  FileText,
  Hash,
  Image as ImageIcon,
  Table as TableIcon,
  Mail,
  Presentation,
  MoreVertical,
  Eye,
  Pencil,
  Copy,
  Move,
  Trash2,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { FluentCard } from '@/components/layout/page-header'
import { Checkbox } from '@/components/ui/checkbox'
import type { ContentItem, ContentItemType } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface ContentItemListProps {
  items: ContentItem[]
  isLoading?: boolean
  selectedItemIds?: Set<string>
  onItemClick?: (item: ContentItem) => void
  onItemDoubleClick?: (item: ContentItem) => void
  onItemView?: (item: ContentItem) => void
  onItemEdit?: (item: ContentItem) => void
  onItemDuplicate?: (item: ContentItem) => void
  onItemMove?: (item: ContentItem) => void
  onItemDelete?: (item: ContentItem) => void
  onSelectionChange?: (selectedIds: Set<string>) => void
  multiSelect?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (field: string) => void
  emptyMessage?: string
  emptyAction?: React.ReactNode
  className?: string
}

type SortField = 'name' | 'type' | 'updatedAt' | 'usageCount'

// ============================================
// Constants
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: TableIcon,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email',
  SlideElement: 'Slide',
}

// ============================================
// ContentItemList Component
// ============================================

export function ContentItemList({
  items,
  isLoading = false,
  selectedItemIds,
  onItemClick,
  onItemDoubleClick,
  onItemView,
  onItemEdit,
  onItemDuplicate,
  onItemMove,
  onItemDelete,
  onSelectionChange,
  multiSelect = false,
  sortBy = 'name',
  sortOrder = 'asc',
  onSort,
  emptyMessage = 'No content items found',
  emptyAction,
  className,
}: ContentItemListProps) {
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set())

  const effectiveSelectedIds = selectedItemIds ?? internalSelectedIds

  const handleItemClick = useCallback(
    (item: ContentItem, e?: React.MouseEvent) => {
      if (multiSelect && e?.ctrlKey) {
        const newSelectedIds = new Set(effectiveSelectedIds)
        if (newSelectedIds.has(item.id)) {
          newSelectedIds.delete(item.id)
        } else {
          newSelectedIds.add(item.id)
        }
        if (onSelectionChange) {
          onSelectionChange(newSelectedIds)
        } else {
          setInternalSelectedIds(newSelectedIds)
        }
      } else {
        if (onSelectionChange) {
          onSelectionChange(new Set([item.id]))
        } else {
          setInternalSelectedIds(new Set([item.id]))
        }
      }

      onItemClick?.(item)
    },
    [multiSelect, effectiveSelectedIds, onSelectionChange, onItemClick]
  )

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allIds = new Set(items.map((item) => item.id))
        if (onSelectionChange) {
          onSelectionChange(allIds)
        } else {
          setInternalSelectedIds(allIds)
        }
      } else {
        if (onSelectionChange) {
          onSelectionChange(new Set())
        } else {
          setInternalSelectedIds(new Set())
        }
      }
    },
    [items, onSelectionChange]
  )

  const handleCheckboxChange = useCallback(
    (item: ContentItem, checked: boolean) => {
      const newSelectedIds = new Set(effectiveSelectedIds)
      if (checked) {
        newSelectedIds.add(item.id)
      } else {
        newSelectedIds.delete(item.id)
      }
      if (onSelectionChange) {
        onSelectionChange(newSelectedIds)
      } else {
        setInternalSelectedIds(newSelectedIds)
      }
    },
    [effectiveSelectedIds, onSelectionChange]
  )

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-neutral-400" />
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
    ) : (
      <ArrowDown className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
    )
  }

  const allSelected = items.length > 0 && items.every((item) => effectiveSelectedIds.has(item.id))
  const someSelected = items.some((item) => effectiveSelectedIds.has(item.id)) && !allSelected

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
        <FileText className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4" />
        <p className="text-neutral-500 dark:text-neutral-400 mb-4">{emptyMessage}</p>
        {emptyAction}
      </div>
    )
  }

  return (
    <FluentCard className={cn('overflow-hidden !p-0', className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            {multiSelect && (
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={allSelected || (someSelected && 'indeterminate')}
                  onCheckedChange={handleSelectAll}
                />
              </th>
            )}
            <th className="text-left px-4 py-3">
              <button
                onClick={() => onSort?.('name')}
                className="flex items-center gap-1 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                Name
                {onSort && getSortIcon('name')}
              </button>
            </th>
            <th className="text-left px-4 py-3 hidden sm:table-cell">
              <button
                onClick={() => onSort?.('type')}
                className="flex items-center gap-1 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                Type
                {onSort && getSortIcon('type')}
              </button>
            </th>
            <th className="text-left px-4 py-3 hidden md:table-cell">
              <button
                onClick={() => onSort?.('updatedAt')}
                className="flex items-center gap-1 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                Updated
                {onSort && getSortIcon('updatedAt')}
              </button>
            </th>
            <th className="text-left px-4 py-3 hidden lg:table-cell">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Tags
              </span>
            </th>
            <th className="text-left px-4 py-3 hidden xl:table-cell">
              <button
                onClick={() => onSort?.('usageCount')}
                className="flex items-center gap-1 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                Usage
                {onSort && getSortIcon('usageCount')}
              </button>
            </th>
            <th className="w-12 px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {items.map((item) => {
            const Icon = TYPE_ICONS[item.type] || FileText
            const color = TYPE_COLORS[item.type] || 'from-gray-400 to-gray-600'
            const typeLabel = TYPE_LABELS[item.type] || item.type
            const isSelected = effectiveSelectedIds.has(item.id)

            return (
              <ContextMenu key={item.id}>
                <ContextMenuTrigger asChild>
                  <tr
                    onClick={(e) => handleItemClick(item, e)}
                    onDoubleClick={() => onItemDoubleClick?.(item)}
                    className={cn(
                      'hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer transition-colors',
                      isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                    )}
                  >
                    {multiSelect && (
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleCheckboxChange(item, checked as boolean)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={cn('p-2 rounded-lg bg-gradient-to-br', color)}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-neutral-900 dark:text-white block truncate">
                            {item.name}
                          </span>
                          {item.description && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 block truncate">
                              {item.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-sm text-neutral-600 dark:text-neutral-400">
                        {typeLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {new Date(item.updatedAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {item.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                          >
                            {tag}
                          </span>
                        ))}
                        {item.tags.length > 2 && (
                          <span className="text-xs text-neutral-400">
                            +{item.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {item.usageCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4 text-neutral-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {onItemView && (
                            <DropdownMenuItem onClick={() => onItemView(item)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                          )}
                          {onItemEdit && (
                            <DropdownMenuItem onClick={() => onItemEdit(item)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {onItemDuplicate && (
                            <DropdownMenuItem onClick={() => onItemDuplicate(item)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                          )}
                          {onItemMove && (
                            <DropdownMenuItem onClick={() => onItemMove(item)}>
                              <Move className="h-4 w-4 mr-2" />
                              Move
                            </DropdownMenuItem>
                          )}
                          {onItemDelete && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => onItemDelete(item)}
                                className="text-red-600 dark:text-red-400"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                </ContextMenuTrigger>

                <ContextMenuContent className="w-48">
                  {onItemView && (
                    <ContextMenuItem onClick={() => onItemView(item)}>
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </ContextMenuItem>
                  )}
                  {onItemEdit && (
                    <ContextMenuItem onClick={() => onItemEdit(item)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </ContextMenuItem>
                  )}
                  {onItemDuplicate && (
                    <ContextMenuItem onClick={() => onItemDuplicate(item)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </ContextMenuItem>
                  )}
                  {onItemMove && (
                    <ContextMenuItem onClick={() => onItemMove(item)}>
                      <Move className="h-4 w-4 mr-2" />
                      Move
                    </ContextMenuItem>
                  )}
                  {onItemDelete && (
                    <>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        onClick={() => onItemDelete(item)}
                        className="text-red-600 dark:text-red-400"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </ContextMenuItem>
                    </>
                  )}
                </ContextMenuContent>
              </ContextMenu>
            )
          })}
        </tbody>
      </table>
    </FluentCard>
  )
}

export default ContentItemList
